// Predator pressure (REFOCUS-2026-09.md §3: Veteran only, tied to the dusk
// coop-lock chore). Who comes for the birds depends on where the player lives,
// so the roster is data (data/predators.json) and only the risk model lives
// here.
//
// This module is deliberately pure: it takes a description of the yard's
// defences and returns what got in. Wiring to the sim clock lives elsewhere.

import raw from '../data/predators.json';
import type { Rng } from '../core/rng';

export type RegionId = 'pr' | 'us_mainland' | 'uk_eu' | 'au';
export type PredatorActivity = 'day' | 'night';
/** What the animal actually came for — a rat will not fight a hen for sport. */
export type PredatorTarget = 'birds' | 'chicks' | 'eggs';

/** Which defences shut this animal out. A false means it does not care. */
export interface PredatorDefenses {
  lockedCoop: boolean;
  hardwareCloth: boolean;
  /** Fence height in inches that turns it back. 0 means a fence is irrelevant. */
  fenceInches: number;
  overhead: boolean;
}

export interface Predator {
  id: string;
  name: { en: string; es: string };
  active: PredatorActivity;
  targets: PredatorTarget;
  baseRisk: number;
  /** The opening it fits through. Under an inch is why hardware cloth exists. */
  gapInches: number;
  stoppedBy: PredatorDefenses;
  digs: boolean;
  climbs?: boolean;
  /** The encyclopedia entry, unlocked the morning after this one gets in. */
  fact: string;
}

export interface Region {
  id: RegionId;
  name: { en: string; es: string };
  predators: string[];
}

export const PREDATORS: Predator[] = raw.predators as Predator[];
export const REGIONS: Region[] = raw.regions as Region[];

const BY_ID = new Map(PREDATORS.map((p) => [p.id, p]));

export function predatorsFor(region: RegionId): Predator[] {
  const found = REGIONS.find((r) => r.id === region);
  if (!found) return [];
  return found.predators.map((id) => BY_ID.get(id)).filter((p): p is Predator => Boolean(p));
}

/**
 * Best guess at a region from the browser locale, so a first-time player in
 * Ohio is not told a mongoose took their hens. Puerto Rico is the default
 * because it is who the game is built for; the player can always change it.
 */
export function regionFromLocale(locale: string): RegionId {
  const tag = locale.toLowerCase();
  if (tag.includes('-pr') || tag.includes('-do') || tag.includes('-cu')) return 'pr';
  if (tag.includes('-us')) return 'us_mainland';
  if (tag.includes('-gb') || tag.includes('-ie') || tag.includes('-de') || tag.includes('-fr'))
    return 'uk_eu';
  if (tag.includes('-au') || tag.includes('-nz')) return 'au';
  return 'pr';
}

/** Everything about the yard that changes whether something gets in tonight. */
export interface YardDefense {
  /** Every coop door shut. The single biggest term, because it is the chore. */
  coopLocked: boolean;
  /** The run is half-inch hardware cloth, not chicken wire. */
  hardwareCloth: boolean;
  /** Lowest fence segment, inches. A perimeter is only as good as its worst part. */
  fenceInches: number;
  /** Roof, shade sail, or coop the flock can get under. */
  overheadCover: boolean;
  /**
   * Aviary netting stretched over the run — the complete answer to an aerial
   * hunter, as opposed to cover the birds have to be standing under.
   */
  overheadNet: boolean;
  /** Feed on the floor. Draws rats, and what draws rats draws everything else. */
  spilledFeed: boolean;
  birds: number;
  chicks: number;
  eggsInNest: number;
}

/**
 * A defence that works cuts the risk by this much — not to zero. A locked coop
 * is not a vault, and telling a player otherwise would be the wrong lesson.
 */
const DEFENSE_FACTOR = 0.15;

/**
 * Chance the animal comes to have a look tonight. This is about what the yard
 * smells like, not how well it is built — a fence has never stopped anything
 * from being interested.
 */
export function drawChance(predator: Predator, yard: YardDefense): number {
  if (predator.targets === 'chicks' && yard.chicks === 0) return 0;
  if (predator.targets === 'eggs' && yard.eggsInNest === 0) return 0;
  if (predator.targets === 'birds' && yard.birds === 0) return 0;

  // More birds is more scent and more noise. Spilled feed is the loudest
  // signal of all, and it is the one thing entirely within the player's control.
  let draw = predator.baseRisk * (1 + Math.min(yard.birds, 12) * 0.04);
  if (yard.spilledFeed) draw *= 1.35;
  return Math.min(draw, 0.95);
}

/**
 * Given that it has come to look, the chance it actually gets in. This is
 * purely about how the yard is built, which is what makes it the teachable half.
 */
export function breachChance(predator: Predator, yard: YardDefense): number {
  let through = 1;
  const stops = predator.stoppedBy;

  if (stops.lockedCoop && yard.coopLocked) through *= DEFENSE_FACTOR;
  if (stops.hardwareCloth && yard.hardwareCloth) through *= DEFENSE_FACTOR;
  if (stops.fenceInches > 0 && yard.fenceInches >= stops.fenceInches) through *= DEFENSE_FACTOR;
  // Overhead is the one axis where a second layer exists, and it has to, or a
  // hawk is unbeatable. Every other predator can be met with two independent
  // defences that each multiply the risk down; an aerial hunter is stopped only
  // by `overhead`, so with cover as the single option its residual sat near
  // 15% a night whatever else the player built — a majority of well-defended
  // 60-day veteran runs lost a bird to one. Shade is cover the bird has to be
  // standing under; netting is over the whole run, and stacks.
  if (stops.overhead && yard.overheadCover) through *= DEFENSE_FACTOR;
  if (stops.overhead && yard.overheadNet) through *= DEFENSE_FACTOR;

  // A digger treats a tall fence as a suggestion unless the ground is closed
  // off too, which in this model means hardware cloth aproned along the line.
  if (predator.digs && stops.fenceInches > 0 && yard.fenceInches >= stops.fenceInches) {
    if (!yard.hardwareCloth) through *= 2.5;
  }
  return clampRisk(through);
}

/** Overall nightly risk, for the dashboard and for reasoning about a yard. */
export function attackRisk(predator: Predator, yard: YardDefense): number {
  return drawChance(predator, yard) * breachChance(predator, yard);
}

function clampRisk(value: number): number {
  return Math.max(0, Math.min(value, 0.95));
}

/**
 * What the yard remembers about each animal. Real predation is not a fresh coin
 * flip every night: something that finds food comes back, and something that
 * hits a wall three nights running goes and bothers someone else. Memoryless
 * risk also makes a ranked veteran run turn on a fluke, which reads as unfair
 * however correct the maths is.
 */
export interface PredatorMemory {
  /** How interested this animal currently is, 0 upward. */
  interest: number;
  /** Consecutive nights it has tried and failed. */
  failures: number;
}

export type PredatorPressure = Record<string, PredatorMemory>;

/** Failures in a row before it writes the yard off and the interest clears. */
export const GIVE_UP_AFTER = 3;
/** Interest decays this much on a night it does not come at all. */
const INTEREST_DECAY = 0.25;

export interface PredatorAttempt {
  predator: Predator;
  /** It got past the defences, not merely that it showed up. */
  breached: boolean;
}

/**
 * Resolves one time-of-day's worth of predator pressure, updating `pressure` in
 * place. At most one animal makes an attempt — a yard losing to three things in
 * one night is not a lesson, it is a punishment.
 */
export function rollPredators(
  region: RegionId,
  when: PredatorActivity,
  yard: YardDefense,
  pressure: PredatorPressure,
  rng: Rng,
): PredatorAttempt | null {
  const candidates = predatorsFor(region).filter((p) => p.active === when);
  let attempt: PredatorAttempt | null = null;

  for (const predator of candidates) {
    const memory = pressure[predator.id] ?? { interest: 0, failures: 0 };
    const draw = drawChance(predator, yard) * (1 + memory.interest);

    if (attempt || draw <= 0 || !rng.chance(Math.min(draw, 0.95))) {
      // Interest fades on a night it stays away.
      memory.interest = Math.max(0, memory.interest - INTEREST_DECAY);
      pressure[predator.id] = memory;
      continue;
    }

    const breached = rng.chance(breachChance(predator, yard));
    if (breached) {
      // It found food here. It will be back, and keener.
      memory.interest += 0.6;
      memory.failures = 0;
    } else {
      memory.failures++;
      memory.interest =
        memory.failures >= GIVE_UP_AFTER ? 0 : memory.interest + 0.3;
      if (memory.failures >= GIVE_UP_AFTER) memory.failures = 0;
    }
    pressure[predator.id] = memory;
    attempt = { predator, breached };
  }

  return attempt;
}
